// @fastn/core - alert metric catalog + delivery-channel model.
//
// Single source of truth shared by the API alert routes (validation + the
// metric/channel catalog endpoints), the event-service evaluator (which metric
// to compute), and the dashboard/embed UI (what to render in the picker).
// Metrics an end-user can alert on. Every key maps to a scalar the evaluator
// computes over (org, end_org, window) from the same tables that back the
// widget-insights view, so what you alert on matches what you see. Grouped so
// the picker can show related metrics together.
export const ALERT_METRICS = [
    // Reliability - is it working?
    { key: "error_rate", label: "Error rate", unit: "percent", group: "Reliability", defaultCondition: "gt", suggestedThreshold: 5, description: "Percent of runs that failed in the window." },
    { key: "success_rate", label: "Success rate", unit: "percent", group: "Reliability", defaultCondition: "lt", suggestedThreshold: 95, description: "Percent of runs that succeeded - alert when it drops." },
    { key: "failed_runs", label: "Failed runs", unit: "count", group: "Reliability", defaultCondition: "gt", suggestedThreshold: 5, description: "Number of runs that failed in the window." },
    // Activity - is data flowing?
    { key: "runs", label: "Total runs", unit: "count", group: "Activity", defaultCondition: "lt", suggestedThreshold: 1, description: "Number of runs in the window - alert when activity drops off." },
    { key: "records_synced", label: "Records synced", unit: "count", group: "Activity", defaultCondition: "lt", suggestedThreshold: 1, description: "Records successfully synced - alert when a sync stalls." },
    { key: "records_failed", label: "Records failed", unit: "count", group: "Activity", defaultCondition: "gt", suggestedThreshold: 0, description: "Records that failed to sync in the window." },
    // Performance - is it fast?
    { key: "avg_run_duration", label: "Avg run time", unit: "ms", group: "Performance", defaultCondition: "gt", suggestedThreshold: 5000, description: "Average run duration in the window." },
    { key: "p95_run_duration", label: "p95 run time", unit: "ms", group: "Performance", defaultCondition: "gt", suggestedThreshold: 10000, description: "95th-percentile (slowest) run duration - catches tail latency." },
    // Health - are connections & workflows OK?
    { key: "broken_connectors", label: "Broken connectors", unit: "count", group: "Health", defaultCondition: "gt", suggestedThreshold: 0, description: "Connections that are not active right now." },
    { key: "dead_workflows", label: "Inactive workflows", unit: "count", group: "Health", defaultCondition: "gt", suggestedThreshold: 0, description: "Enabled workflows that have not run in the window." },
];
export const ALERT_METRIC_KEYS = ALERT_METRICS.map((m) => m.key);
export function alertMetricDef(key) {
    return ALERT_METRICS.find((m) => m.key === key);
}
export const ALERT_CONDITIONS = ["gt", "lt"];
/**
 * How a rule fires:
 *   threshold - the evaluator polls the metric over the window and fires on a
 *               breach (the original behavior).
 *   instant   - the failure watcher fires on each failed/timeout execution,
 *               within ~a minute of the failure, with the workflow name, the
 *               classified reason, and a deep link. metric/condition/threshold/
 *               window are not evaluated for instant rules.
 */
export const ALERT_MODES = ["threshold", "instant"];
/**
 * Metrics computed from workflow_executions, and therefore scopable to a set
 * of workflows via alert_rules.workflow_ids. The sync_jobs / connection /
 * workflows metrics have no per-execution grain, so a workflow scope on them
 * is rejected at the API rather than silently ignored.
 */
export const WORKFLOW_SCOPABLE_METRICS = [
    "runs", "failed_runs", "error_rate", "success_rate", "avg_run_duration", "p95_run_duration",
];
export const ALERT_WINDOWS = ["1h", "24h", "7d", "30d"];
/** Postgres interval literal for a window (used by the evaluator's queries). */
export function alertWindowToInterval(w) {
    switch (w) {
        case "1h": return "1 hour";
        case "24h": return "24 hours";
        case "7d": return "7 days";
        case "30d": return "30 days";
        default: return "24 hours";
    }
}
export const ALERT_DELIVERY_TYPES = ["email", "webhook", "slack", "connection"];
/**
 * Connectors we know how to deliver to, mapped to the send action + the field
 * that carries the destination and the message body. The evaluator uses this
 * to turn a { type:'connection' } descriptor into a connector action call.
 */
export const ALERT_CONNECTION_SENDERS = {
    slack: { action: "sendMessage", targetField: "channel", messageField: "text", label: "Slack" },
    discord: { action: "sendMessage", targetField: "channelId", messageField: "content", label: "Discord" },
    telegram: { action: "sendMessage", targetField: "chatId", messageField: "text", label: "Telegram" },
};
/** Supported delivery apps, for the UI to show even before one is connected. */
export const ALERT_SENDER_APPS = Object.entries(ALERT_CONNECTION_SENDERS).map(([slug, s]) => ({ slug, label: s.label, targetField: s.targetField }));
//# sourceMappingURL=alerts.js.map